/**
 * Frontend HTTP client for /api/conversations.
 *
 * Durable, cross-device conversation history. The frontend keeps a
 * localStorage copy as the offline cache and mirrors the DISPLAY transcript
 * here so a conversation survives a sandbox wipe and appears on other devices.
 *
 * All methods are best-effort — the UI never blocks on them. If the endpoint
 * is unreachable the user still has the localStorage-only Conversations panel.
 */
import { getBootstrapToken } from './sso';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

export interface StoredDisplayMsg {
  role: 'user' | 'assistant';
  text: string;
  toolCalls: { name: string; id?: string; input?: unknown }[];
}

export interface ConversationTranscript {
  title: string;
  display: StoredDisplayMsg[];
  workbookFileName?: string | null;
  messageCount: number;
}

export interface ConversationListItem {
  conversationId: string;
  title: string;
  messageCount: number;
  workbookFileName: string | null;
  sourceUpdatedAt: string;
  savedAt: string;
  expiresAt: string;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getBootstrapToken();
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
    credentials: 'include',
  });
}

export async function putConversation(
  conversationId: string,
  transcript: ConversationTranscript,
  sourceUpdatedAt: string,
): Promise<boolean> {
  try {
    const resp = await authedFetch('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ conversationId, transcript, sourceUpdatedAt }),
    });
    if (!resp.ok) {
      console.warn(`[conversationApi] put failed ${resp.status}`);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[conversationApi] put network error', (e as Error).message);
    return false;
  }
}

export async function listConversations(): Promise<ConversationListItem[]> {
  try {
    const resp = await authedFetch('/api/conversations', { method: 'GET' });
    if (!resp.ok) return [];
    const body = (await resp.json()) as { items?: ConversationListItem[] };
    return body.items ?? [];
  } catch {
    return [];
  }
}

export async function fetchConversation(conversationId: string): Promise<ConversationTranscript | null> {
  try {
    const resp = await authedFetch(`/api/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'GET',
    });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { transcript?: ConversationTranscript };
    return body.transcript ?? null;
  } catch {
    return null;
  }
}

export async function deleteConversation(conversationId: string): Promise<boolean> {
  try {
    const resp = await authedFetch(`/api/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    });
    return resp.ok;
  } catch {
    return false;
  }
}
